<template>
  <div class="bkash-status">
    <div class="bkash-status__card">
      <!-- Status icon -->
      <div class="bkash-status__icon-wrap" :style="{ background: config.iconBg }">
        <img v-if="stateType === 'success'" :src="successIcon" width="44" height="44" alt="" />
        <img
          v-else-if="stateType === 'processing'"
          :src="clockIcon"
          width="33"
          height="33"
          alt="" />
        <img v-else-if="stateType === 'expired'" :src="circleIcon" width="44" height="44" alt="" />
        <img v-else :src="triangleIcon" width="32" height="29" alt="" />
      </div>

      <!-- Title & subtitle -->
      <div class="bkash-status__title">{{ $t(config.titleKey) }}</div>
      <div class="bkash-status__subtitle">{{ $t(config.subtitleKey) }}</div>

      <div class="bkash-status__spacer" />

      <!-- Payment details card -->
      <div class="bkash-status__details">
        <div class="bkash-status__details-title">{{ $t('bkash_status__payment_details') }}</div>

        <div class="bkash-status__details-row">
          <span class="bkash-status__details-label">{{ $t(config.dateLabelKey) }}</span>
          <span class="bkash-status__details-value">{{ formattedDate }}</span>
        </div>

        <div class="bkash-status__details-row">
          <span class="bkash-status__details-label">{{ $t('bkash__amount_label') }}</span>
          <span class="bkash-status__details-value">{{ formattedAmount }}</span>
        </div>

        <div
          v-if="config.showReference && paymentInvoice.reference_id"
          class="bkash-status__details-row">
          <span class="bkash-status__details-label">{{ $t('bkash_status__invoice_ref') }}</span>
          <span class="bkash-status__details-value bkash-status__details-value--mono">
            {{ paymentInvoice.reference_id }}
          </span>
        </div>
      </div>

      <div class="bkash-status__card-bottom">
        <button class="bkash-status__return-btn" type="button" @click="redirectToMerchantSite">
          {{ $t('bkash_status__return_btn') }}
        </button>

        <div class="bkash-status__footer">
          <a class="bkash-status__footer-phone" :href="`tel:${supportPhone}`">
            <div class="bkash-status__footer-phone-icon">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.5 1h2l1 2.5-1.25 1a6.5 6.5 0 0 0 4.25 4.25L9.5 7.5 12 8.5v2A1 1 0 0 1 11 11.5C5.2 11.5.5 6.8.5 1A1 1 0 0 1 1.5 0z"
                  fill="#ffffff" />
              </svg>
            </div>
            <span>{{ supportPhone }}</span>
          </a>
          <div class="bkash-status__footer-copy">{{ $t('bkash_status__footer_text') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { MoneyFormatter } from '@/helpers/Formatting/MoneyFormatter';
import ClockIcon from '../assets/clock.svg';
import SuccessIcon from '../assets/success.svg';
import CircleIcon from '../assets/circle.svg';
import TriangleIcon from '../assets/triangle.svg';

const STATUS_TYPE_MAP = {
  processed: 'success',
  authorized: 'success',
  partially_processed: 'success',
  refunded: 'success',
  created: 'processing',
  invoked: 'processing',
  invoke_pending: 'processing',
  process_pending: 'processing',
  expired: 'expired',
  process_failed: 'failed',
  invoke_failed: 'failed',
  terminated: 'failed',
  voided: 'failed',
  charged_back: 'failed',
  revoked: 'failed',
};

const SUPPORT_PHONE = '16247';

const STATE_CONFIG = {
  success: {
    titleKey: 'bkash_status__success_title',
    subtitleKey: 'bkash_status__success_subtitle',
    dateLabelKey: 'bkash_status__payment_date',
    iconBg: 'rgba(56,142,60,0.10)',
    showReference: true,
  },
  processing: {
    titleKey: 'bkash_status__processing_title',
    subtitleKey: 'bkash_status__processing_subtitle',
    dateLabelKey: 'bkash_status__initiation_date',
    iconBg: 'rgba(245,147,0,0.10)',
    showReference: false,
  },
  expired: {
    titleKey: 'bkash_status__expired_title',
    subtitleKey: 'bkash_status__expired_subtitle',
    dateLabelKey: 'bkash_status__initiation_date',
    iconBg: 'rgba(211,47,47,0.10)',
    showReference: false,
  },
  failed: {
    titleKey: 'bkash_status__failed_title',
    subtitleKey: 'bkash_status__failed_subtitle',
    dateLabelKey: 'bkash_status__payment_date',
    iconBg: 'rgba(211,47,47,0.10)',
    showReference: false,
  },
};

export default {
  name: 'BkashStatusPage',

  data() {
    return {
      clockIcon: ClockIcon,
      successIcon: SuccessIcon,
      circleIcon: CircleIcon,
      triangleIcon: TriangleIcon,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData']),
    ...mapState('InvoiceLifetime', ['isExpired']),

    supportPhone: () => SUPPORT_PHONE,

    paymentInvoice() {
      return this.paymentInvoiceData?.data || this.paymentInvoiceData || {};
    },

    currentStatus() {
      return this.paymentInvoice.status || '';
    },

    stateType() {
      return this.isExpired ? 'expired' : STATUS_TYPE_MAP[this.currentStatus] || 'processing';
    },

    config() {
      return STATE_CONFIG[this.stateType] || STATE_CONFIG.processing;
    },

    formattedDate() {
      const ts = this.paymentInvoice.created;
      if (!ts) return '—';
      return this.$dayjs(ts * 1000).format('DD.MM.YYYY HH:mm');
    },

    formattedAmount() {
      const inv = this.paymentInvoice;
      const amount =
        ['partially_processed', 'processed'].includes(inv.status) && inv.processed_amount != null
          ? inv.processed_amount
          : inv.amount;
      if (amount == null) return '—';
      return new MoneyFormatter().format(amount, inv.currency, true);
    },
  },

  methods: {
    redirectToMerchantSite() {
      this.$emit('redirect-to-merchant');
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

$bkash-text: #202529;
$bkash-text-sub: #959395;
$bkash-border: #f3f1f3;

.bkash-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  background: #ffffff;
  padding: 24px 16px;
  font-family: 'Inter', sans-serif;
  box-sizing: border-box;

  @media (max-width: 520px) {
    padding: 0;
    background: #ffffff;
  }

  &__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 520px;
    flex: 1;
    max-height: 720px;
    background: #ffffff;
    border-radius: 32px;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.07);
    padding: 52px 32px 0;
    box-sizing: border-box;
    margin-top: 15%;

    @media (max-width: 520px) {
      max-width: 100%;
      max-height: none;
      border-radius: 0;
      box-shadow: none;
      padding: 40px 20px 0;
      background: #fdfdfd;
      margin-top: 0;
    }
  }

  &__card-bottom {
    margin-top: auto;
    width: 100%;
    flex-shrink: 0;
    padding: 0 0 0;
  }

  &__spacer {
    flex: 1;
    min-height: 24px;
    max-height: 80px;
  }

  &__icon-wrap {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    flex-shrink: 0;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $bkash-text;
    text-align: center;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 12px;
    font-weight: 400;
    color: $bkash-text-sub;
    text-align: center;
    line-height: 1.5;
  }

  &__details {
    width: 100%;
    background: #ffffff;
    border: 2px solid $bkash-border;
    border-radius: 12px;
    padding: 32px 24px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
    box-sizing: border-box;
  }

  &__details-title {
    font-size: 14px;
    font-weight: 700;
    color: $bkash-text;
    margin-bottom: 20px;
    text-align: start;
  }

  &__details-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }

    &:first-of-type {
      padding-top: 0;
    }
  }

  &__details-label {
    font-family: 'Corbel', 'Candara', 'Calibri', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: $bkash-text;
  }

  &__details-value {
    font-family: 'Corbel', 'Candara', 'Calibri', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: $bkash-text;
    text-align: right;

    &--mono {
      font-family: monospace;
      font-size: 12px;
      letter-spacing: 0.02em;
    }
  }

  &__return-btn {
    display: block;
    width: 100%;
    height: 50px;
    padding: 16px;
    margin-bottom: 16px;
    background: #cf3170;
    border: none;
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s;
    letter-spacing: 0.01em;

    &:hover {
      background: #b50d55;
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    margin-left: -32px;
    margin-right: -32px;
    border-top: 2px solid $bkash-border;
    padding: 14px 32px 12px;

    @media (max-width: 520px) {
      margin-left: -20px;
      margin-right: -20px;
      padding: 14px 20px 12px;
    }
  }

  &__footer-phone {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 500;
    color: #cf3170;
    text-decoration: none;
  }

  &__footer-phone-icon {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #cf3170;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__footer-copy {
    font-size: 11px;
    font-weight: 500;
    color: $bkash-text-sub;
  }
}
</style>
